import React, { Suspense, useEffect, useState } from "react";
import { Switch, Route, Redirect, useRouteMatch } from "react-router-dom";
import HeaderBar from "./components/HeaderBar";
import LoadBoundary from "./components/LoadBoundary";
import LoadingMessage from "./components/LoadingMessage";
import useUser from "./hooks/useUser";
import useShortCode from "./hooks/useShortCode";
import useOrganisationInfo from "./hooks/useOrganisationInfo";
import { SidebarProvider, useSidebar } from "./contexts/SidebarContext";
import { RouteProvider } from "./contexts/RouteContext";
import { BottomNotification } from "./components/BottomNotification";
import { BottomNotificationProvider } from "./components/BottomNotification/bottomNotificationContext";
import BannerControl from "./components/BannerControl";
import BannerRenderer from "./components/BannerControl/BannerRenderer";
import OnboardingChecklist from "./components/OnboardingChecklist";
import OnboardingSurveyModal from "./components/OnboardingSurveyModal";
import SessionTimeout from "./components/SessionTimeout";
import CapitalPlaceholder from "./pages/CapitalPlaceholder";
import FeedbackPage from "./pages/FeedbackPage";
import NotFound from "./pages/NotFound";
import MainWrapper from "./components/MainWrapper";
import StretchStack from "./components/StretchStack";
import OrganisationSideMenu from "./components/OrganisationSideMenu";
import RemoteUnavailable from "./components/RemoteUnavailable";
import Home from "./pages/Home";
import CashflowReceivables from "./app/cashflow";
import CashflowForecasting from "./app/cashflow/forecasting";
import useOrganisationSync from "./hooks/useOrganisationSync";
import useOrganisationSwitchFromQuery from "./hooks/useOrganisationSwitchFromQuery";
import useConnectErrorFromQuery from "./hooks/useConnectErrorFromQuery";
import { SubscriptionTransitionProvider } from "./contexts/SubscriptionTransitionContext";

// ─── Auth / public pages (owned by DRM shell) ────────────────────────────────
import Login from "./app/login/index";
import LoginGoogle from "./app/login-google/index";
import LoginGoogleCallback from "./app/login-google-callback/index";
import LoginMicrosoft from "./app/login-microsoft/index";
import LoginMicrosoftCallback from "./app/login-microsoft-callback/index";
import LoginIntuit from "./app/login-intuit/index";
import LoginIntuitCallback from "./app/login-intuit-callback/index";
import LoginIntuitEmailNotVerified from "./app/login-intuit-email-not-verified/index";
import LoginXero from "./app/login-xero/index.jsx";
import LoginXeroCallback from "./app/login-xero-callback/index";
import LoginXeroAppLauncher from "./app/login-xero-app-launcher/index";
import LoginSage200Callback from "./app/login-sage200-callback/index";
import LoginNylasCallback from "./app/connect-nylas-callback/index";
import AuthNetsuiteCallback from "./app/auth-netsuite-callback/index";
import OrganisationLogin from "./app/organisation-login/index";
import OrganisationAccess from "./app/organisation-access/index";
import RecoverPassword from "./app/recover-password/index";
import NewPassword from "./app/new-password/index";
import Connect from "./app/connect/index";
import Import from "./app/import/index";
import VerifySenderEmail from "./app/verify/sender/index";
import VerifyEmail from "./app/verify/email/index";
import Signup from "./app/signup/index";

// ─── Shell-level authenticated pages (global, not product-specific) ──────────
import Account from "./app/account/index";
import Admin from "./app/admin/index";
import Settings from "./app/settings/index";
import ProtectedRoute from "./components/ProtectedRoute";
import ProtectedOrganisationRoute from "./components/ProtectedOrganisationRoute";
import { Integrations, roles, Features, Modules } from "./util/constants";
import { useConfigureTracking } from "./util/analytics";
import { useConfigureLogging } from "./util/logging";
import AlreadyConnected from "./app/addorganisation/alreadyconnected/index.jsx";

// ─── Federated remotes loaded lazily ─────────────────────────────────────────
const ReceivablesUnavailable = () => <RemoteUnavailable errorCode="REC-503" />;

const ReceivablesApp = React.lazy(() =>
  import("receivables/App").catch((error) => {
    console.error("Failed to load receivables remote app", error);
    return {
      default: ReceivablesUnavailable,
    };
  }),
);

const FederatedFallback = () => <LoadingMessage center useLogoAnimation />;

const ReceivablesRoute = () => (
  <Suspense fallback={<FederatedFallback />}>
    <ReceivablesApp />
  </Suspense>
);

const SidebarLayoutWrapper = ({ showSidebar, showImport, children }) => {
  const { isManuallyCollapsed } = useSidebar();
  return (
    <div
      className={`main-wrapper ${showSidebar ? "has-sidebar" : ""} ${showSidebar && isManuallyCollapsed ? "sidebar-collapsed" : ""}`}
      style={{ display: "flex", flex: 1 }}
    >
      {showSidebar && <OrganisationSideMenu showImport={showImport} />}
      {children}
    </div>
  );
};

/**
 * DRM Shell
 *
 * Owns: HeaderBar, top-level navigation, session context, federation shell.
 * Loads product pillars as federated remotes:
 *   /receivables/* → chaser-client (ReceivablesApp)
 *   /cashflow/*    → cash-flow-client (CashflowApp)
 *   /capital/*     → Access to Capital placeholder (served locally)
 */
const App = () => {
  const user = useUser();
  const { isLoggedIn } = user;
  const shortCode = useShortCode();
  const organisation = useOrganisationInfo();
  const [onboardingSurveyOpen, setOnboardingSurveyOpen] = useState(false);

  const {
    hasOrganisations,
    expiredTrial,
    isSuspended,
    expiredToken,
    noChase,
    accountingSoftware,
    integration,
    lastRefreshDate,
  } = organisation;

  const publicMatch = useRouteMatch({
    path: [
      "/auth",
      "/login",
      "/signup",
      "/sender-verify",
      "/verify-email",
      "/recover",
      "/new-password",
    ],
    exact: false,
  });
  const isOnPublicPath = !!publicMatch;

  const welcomeMatch = useRouteMatch({
    path: [
      "/addorganisation/csvconfiguration",
      "/totp",
      "/select-organisations",
    ],
    exact: false,
  });

  const cashflowMatch = useRouteMatch({
    path: ["/cashflow"],
    exact: false,
  });
  const isCashflowFeedbackPath =
    location.pathname.startsWith("/cashflow/feedback");

  const switchOrgs = !welcomeMatch;
  const showLinks = !welcomeMatch;
  const showBottomNotification = isLoggedIn && !isOnPublicPath && !expiredTrial;
  const showImport =
    isLoggedIn && hasOrganisations && accountingSoftware === Integrations.CSV;

  const showSidebar =
    isLoggedIn && !isOnPublicPath && showLinks && hasOrganisations;

  const syncAction = useOrganisationSync({
    shortCode,
    isLoggedIn,
    hasOrganisations,
    expiredToken,
    lastRefreshDate,
  });

  useOrganisationSwitchFromQuery({ user, isLoggedIn });
  useConnectErrorFromQuery({ isLoggedIn });

  useEffect(() => {
    window.__CHASER_SHELL_LOADED__ = true;
  }, []);

  useEffect(() => {
    if (!organisation?._id) return;
    if (!organisation.hasConfirmedDetails) {
      setOnboardingSurveyOpen(true);
    }
  }, [organisation?._id, organisation?.hasConfirmedDetails]);

  useEffect(() => {
    if (location.hash) return;
    window.scrollTo(0, 0);
  }, [location.pathname, location.search, location.hash]);

  // Analytics & error tracking — DRM shell owns Pendo/HubSpot/Sentry init.
  useConfigureTracking({ organisation, user });
  useConfigureLogging({ shortCode, user });

  return (
    <BannerRenderer>
      <SubscriptionTransitionProvider>
        <SidebarProvider>
          <RouteProvider>
            {!isOnPublicPath && isLoggedIn && (
              <header
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100%",
                  zIndex: 4,
                }}
              >
                <HeaderBar
                  showLinks={showLinks}
                  switchOrgs={switchOrgs}
                  syncAction={syncAction}
                />
              </header>
            )}

            <MainWrapper
              showVerifyEmailBanner={false}
              isOnPublicPath={isOnPublicPath}
            >
              <SidebarLayoutWrapper
                showSidebar={showSidebar}
                showImport={showImport}
              >
                <StretchStack
                  as="main"
                  flex="1"
                  style={
                    cashflowMatch && !isCashflowFeedbackPath
                      ? { maxWidth: "100%", margin: "0 auto" }
                      : undefined
                  }
                >
                  <BottomNotificationProvider value={showBottomNotification}>
                    <LoadBoundary center>
                      <Switch>
                        {/* ─── Auth / public routes (DRM owns these) ───────────── */}
                        <Route exact path="/login" component={Login} />
                        <Route exact path="/signup" component={Signup} />
                        <Route
                          exact
                          path="/recover"
                          component={RecoverPassword}
                        />
                        <Route
                          exact
                          path="/new-password"
                          component={NewPassword}
                        />
                        <Route
                          exact
                          path="/sender-verify"
                          component={VerifySenderEmail}
                        />
                        <Route
                          exact
                          path="/verify-email"
                          component={VerifyEmail}
                        />
                        <Route
                          exact
                          path="/auth/google"
                          component={LoginGoogle}
                        />
                        <Route
                          exact
                          path="/auth/google/callback"
                          component={LoginGoogleCallback}
                        />
                        <Route
                          exact
                          path="/auth/microsoft"
                          component={LoginMicrosoft}
                        />
                        <Route
                          exact
                          path="/auth/microsoft/callback"
                          component={LoginMicrosoftCallback}
                        />
                        <Route
                          exact
                          path="/auth/intuit"
                          component={LoginIntuit}
                        />
                        <Route
                          exact
                          path="/auth/intuit/callback"
                          component={LoginIntuitCallback}
                        />
                        <Route
                          exact
                          path="/auth/intuit/email-not-verified"
                          component={LoginIntuitEmailNotVerified}
                        />
                        <Route exact path="/auth/xero" component={LoginXero} />
                        <Route
                          exact
                          path="/auth/xero/callback"
                          component={LoginXeroCallback}
                        />
                        <Route
                          exact
                          path="/auth/xero/app-launcher"
                          component={LoginXeroAppLauncher}
                        />
                        <Route
                          exact
                          path="/auth/sage200/callback"
                          component={LoginSage200Callback}
                        />
                        <Route
                          exact
                          path="/auth/nylas/callback"
                          component={LoginNylasCallback}
                        />
                        <Route
                          exact
                          path="/auth/netsuite/callback"
                          component={AuthNetsuiteCallback}
                        />
                        <Route
                          exact
                          path="/quickbooks/email-not-verified"
                          component={LoginIntuitEmailNotVerified}
                        />
                        <ProtectedRoute
                          path="/o/:shortCode/access"
                          component={OrganisationAccess}
                        />
                        <Route
                          path="/o/:shortCode"
                          component={OrganisationLogin}
                        />

                        {/* ─── Shell-level authenticated pages (global across all apps) ─── */}
                        <ProtectedRoute
                          path="/user"
                          component={Account}
                          allowedWithoutTotp
                        />
                        <ProtectedRoute
                          path="/connect"
                          component={Connect}
                          allowedWithoutTotp
                        />
                        <ProtectedOrganisationRoute
                          path="/import"
                          component={Import}
                        />
                        <ProtectedOrganisationRoute
                          path="/settings"
                          component={Settings}
                        />
                        <ProtectedOrganisationRoute
                          path="/admin"
                          component={Admin}
                          requiredRoles={[roles.admin]}
                        />

                        {/* ─── DRM-owned feedback pages ───────────────────────── */}
                        <ProtectedOrganisationRoute
                          exact
                          path="/receivables/feedback"
                          component={FeedbackPage}
                          moduleName={Modules.RECEIVABLES}
                        />
                        <ProtectedOrganisationRoute
                          exact
                          path="/cashflow/feedback"
                          component={FeedbackPage}
                          moduleName={Modules.CASHFLOW}
                        />

                        {/* ─── Cash Flow gate routes (DRM-owned wrappers) ─────── */}
                        <ProtectedOrganisationRoute
                          path={[
                            "/cashflow/receivables",
                            "/cashflow/receivables/*",
                          ]}
                          component={CashflowReceivables}
                          moduleName={Modules.RECEIVABLES}
                        />
                        <ProtectedOrganisationRoute
                          path={["/cashflow/forecast", "/cashflow/forecast/*"]}
                          component={CashflowForecasting}
                          moduleName={Modules.CASHFLOW}
                          requiredRoles={[
                            roles.owner,
                            roles.manager,
                            roles.cashflowManager,
                          ]}
                        />

                        {/* ─── Receivables (chaser-client remote) ──────────────── */}
                        <ProtectedOrganisationRoute
                          path="/receivables"
                          component={ReceivablesRoute}
                          moduleName={Modules.RECEIVABLES}
                        />

                        {/* ─── Receivables legacy top-level routes ──────────────── */}
                        <ProtectedRoute
                          path="/messages/:messageId/validate"
                          component={ReceivablesRoute}
                          moduleName={Modules.RECEIVABLES}
                        />
                        <ProtectedRoute
                          path="/addorganisation/csvconfiguration"
                          component={ReceivablesRoute}
                          allowedWithoutTotp
                        />
                        <ProtectedRoute
                          path="/select-organisations/sage200"
                          component={ReceivablesRoute}
                          allowedWithoutTotp
                        />
                        <ProtectedRoute
                          path="/select-organisations/sage"
                          exact
                          component={ReceivablesRoute}
                          allowedWithoutTotp
                        />
                        <ProtectedRoute
                          path="/select-organisations/netsuite"
                          component={ReceivablesRoute}
                          allowedWithoutTotp
                        />
                        <ProtectedRoute
                          path="/addorganisation/alreadyconnected"
                          component={AlreadyConnected}
                          allowedWithoutTotp
                        />

                        <Route
                          path="/dashboard"
                          render={({ location: routeLocation }) => {
                            const legacyPathSuffix =
                              routeLocation.pathname.replace(
                                /^\/dashboard/,
                                "",
                              );
                            return (
                              <Redirect
                                to={`/receivables${legacyPathSuffix}${routeLocation.search || ""}`}
                              />
                            );
                          }}
                        />

                        <Route
                          path="/invoice"
                          render={({ location: routeLocation }) => {
                            const legacyPathSuffix =
                              routeLocation.pathname.replace(/^\/invoice/, "");
                            return (
                              <Redirect
                                to={`/receivables/invoice${legacyPathSuffix}${routeLocation.search || ""}`}
                              />
                            );
                          }}
                        />

                        <Route
                          path="/customer"
                          render={({ location: routeLocation }) => {
                            const legacyPathSuffix =
                              routeLocation.pathname.replace(/^\/customer/, "");
                            return (
                              <Redirect
                                to={`/receivables/customer${legacyPathSuffix}${routeLocation.search || ""}`}
                              />
                            );
                          }}
                        />

                        {/* ─── Access Capital fake door (served locally) ───────── */}
                        <ProtectedOrganisationRoute
                          path="/capital"
                          component={CapitalPlaceholder}
                        />

                        {/* ─── Root redirect ───────────────────────────────────── */}
                        <ProtectedOrganisationRoute
                          exact
                          path="/"
                          component={Home}
                        />

                        <ProtectedOrganisationRoute
                          exact
                          path="/home"
                          component={Home}
                        />
                        {/* ─── 404 ─────────────────────────────────────────────── */}
                        <Route>
                          <NotFound />
                        </Route>
                      </Switch>
                    </LoadBoundary>
                  </BottomNotificationProvider>
                </StretchStack>
              </SidebarLayoutWrapper>
            </MainWrapper>
            {showBottomNotification && shortCode && (
              <BottomNotification
                shortCode={shortCode}
                accountingSoftware={accountingSoftware}
                integration={integration}
                isSuspended={isSuspended}
                expiredToken={expiredToken}
                lastRefreshDate={lastRefreshDate}
                noChase={noChase}
              />
            )}
            {isLoggedIn && shortCode && !isOnPublicPath && !expiredTrial && (
              <OnboardingChecklist />
            )}
            <OnboardingSurveyModal
              open={onboardingSurveyOpen}
              close={() => setOnboardingSurveyOpen(false)}
            />
            {shortCode && <BannerControl />}
            <SessionTimeout />
          </RouteProvider>
        </SidebarProvider>
      </SubscriptionTransitionProvider>
    </BannerRenderer>
  );
};

export default App;
